import { type FormEvent, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useAdminCreateOrg, useAdminUsers } from '@/lib/admin';

export function CreateOrgPanel() {
  const users = useAdminUsers();
  const create = useAdminCreateOrg();

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [prefix, setPrefix] = useState('AR');
  const [ownerId, setOwnerId] = useState<string>('');

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!name.trim()) return;
    try {
      await create.mutateAsync({
        name: name.trim(),
        slug: slug.trim() || undefined,
        part_number_prefix: prefix.trim() || 'AR',
        owner_user_id: ownerId === '' ? null : Number(ownerId),
      });
      setName('');
      setSlug('');
      setPrefix('AR');
      setOwnerId('');
    } catch {
      /* surfaced below */
    }
  };

  return (
    <section className="rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-5">
      <h2 className="text-lg font-semibold">Create organization</h2>
      <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">
        Hand ownership to any user. If you pick someone else, you'll keep an
        owner membership too so you can manage it until they take over.
      </p>

      <form onSubmit={onSubmit} className="mt-4 grid gap-3 md:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">Name</span>
          <Input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">
            Slug (optional)
          </span>
          <Input
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="auto from name"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">
            Part number prefix
          </span>
          <Input
            value={prefix}
            onChange={(e) => setPrefix(e.target.value.toUpperCase())}
            maxLength={16}
            className="font-mono"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">
            Owner (defaults to you)
          </span>
          <select
            value={ownerId}
            onChange={(e) => setOwnerId(e.target.value)}
            className="block w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-2 text-sm"
          >
            <option value="">— me —</option>
            {users.data?.map((u) => (
              <option key={u.id} value={String(u.id)}>
                {u.email}
              </option>
            ))}
          </select>
        </label>

        {create.isError && (
          <p className="md:col-span-2 text-sm text-red-600">
            {(create.error as Error).message}
          </p>
        )}

        <div className="md:col-span-2 flex justify-end">
          <Button
            type="submit"
            disabled={create.isPending || !name.trim()}
          >
            {create.isPending ? 'Creating…' : 'Create organization'}
          </Button>
        </div>
      </form>
    </section>
  );
}
